/**
 * ContextualTips Component
 *
 * Displays relevant tips from validated inspector notes for a given jurisdiction.
 * Part of Phase 4: Contextual delivery in the Inspection Intelligence Pipeline.
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Lightbulb,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Users,
  MessageSquare,
  Loader2,
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useContextualTips, type ContextualTip } from '@/hooks/useInspections';

interface ContextualTipsProps {
  jurisdictionId: number | null;
  trade?: string;
  inspectionTypeId?: string;
  limit?: number;
  /** Default to collapsed for progressive disclosure */
  defaultExpanded?: boolean;
  compact?: boolean;
  /** Number of blockers - auto-expands if > 0 */
  blockerCount?: number;
}

export function ContextualTips({
  jurisdictionId,
  trade,
  inspectionTypeId,
  limit = 5,
  defaultExpanded = false, // Changed: Default collapsed for progressive disclosure
  compact = false,
  blockerCount = 0,
}: ContextualTipsProps) {
  // Auto-expand if blockers exist, otherwise use default
  const [isExpanded, setIsExpanded] = useState(blockerCount > 0 || defaultExpanded);

  const { data: tips = [], isLoading, error } = useContextualTips({
    jurisdictionId,
    trade,
    inspectionTypeId,
    limit,
  });

  // Don't render anything if no jurisdiction selected
  if (!jurisdictionId) return null;

  // Don't render if loading or no tips
  if (isLoading) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading inspector tips...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || tips.length === 0) {
    return null;
  }

  // Get icon based on note type
  const getTipIcon = (noteType: string) => {
    switch (noteType) {
      case 'fail_reason':
        return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      case 'pattern':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'schedule':
        return <Clock className="h-4 w-4 text-purple-500" />;
      default:
        return <Lightbulb className="h-4 w-4 text-yellow-500" />;
    }
  };

  // Get badge variant based on note type
  const getTipBadgeVariant = (noteType: string) => {
    switch (noteType) {
      case 'fail_reason':
        return 'destructive';
      case 'pattern':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  // Get note type label
  const getNoteTypeLabel = (noteType: string) => {
    const labels: Record<string, string> = {
      tip: 'Tip',
      pattern: 'Pattern',
      fail_reason: 'Common Fail',
      preference: 'Preference',
      schedule: 'Schedule',
    };
    return labels[noteType] || noteType;
  };

  if (compact) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Lightbulb className="h-4 w-4 text-yellow-500" />
          Inspector Tips ({tips.length})
        </div>
        <div className="space-y-1">
          {tips.slice(0, 3).map((tip) => (
            <TipCard key={tip.note_id} tip={tip} compact />
          ))}
          {tips.length > 3 && (
            <p className="text-xs text-muted-foreground pl-6">
              +{tips.length - 3} more tips
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <Card className="border-yellow-200 bg-yellow-50/30 dark:bg-yellow-950/10 dark:border-yellow-800">
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-yellow-100/50 dark:hover:bg-yellow-900/20 transition-colors py-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Lightbulb className="h-5 w-5 text-yellow-500" />
                Inspector Tips
                <Badge variant="secondary" className="ml-1 text-xs">
                  {tips.length}
                </Badge>
              </CardTitle>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </div>
            {!isExpanded && (
              <p className="text-xs text-muted-foreground mt-1">
                Community-verified tips for this jurisdiction
              </p>
            )}
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0 space-y-3">
            <p className="text-xs text-muted-foreground">
              Tips from contractors who have passed inspections in this jurisdiction
            </p>
            {tips.map((tip) => (
              <TipCard key={tip.note_id} tip={tip} />
            ))}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

interface TipCardProps {
  tip: ContextualTip;
  compact?: boolean;
}

function TipCard({ tip, compact = false }: TipCardProps) {
  // Get icon based on note type
  const getTipIcon = (noteType: string) => {
    switch (noteType) {
      case 'fail_reason':
        return <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />;
      case 'pattern':
        return <Clock className="h-4 w-4 text-blue-500 flex-shrink-0" />;
      case 'schedule':
        return <Clock className="h-4 w-4 text-purple-500 flex-shrink-0" />;
      default:
        return <Lightbulb className="h-4 w-4 text-yellow-500 flex-shrink-0" />;
    }
  };

  const getNoteTypeLabel = (noteType: string) => {
    const labels: Record<string, string> = {
      tip: 'Tip',
      pattern: 'Pattern',
      fail_reason: 'Common Fail',
      preference: 'Preference',
      schedule: 'Schedule',
    };
    return labels[noteType] || noteType;
  };

  if (compact) {
    return (
      <div className="flex items-start gap-2 p-2 rounded-md bg-muted/50">
        {getTipIcon(tip.note_type)}
        <p className="text-xs text-muted-foreground line-clamp-2">{tip.content}</p>
      </div>
    );
  }

  return (
    <div
      className={`p-3 rounded-lg border ${
        tip.note_type === 'fail_reason'
          ? 'bg-amber-50/50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800'
          : 'bg-white/60 border-gray-100 dark:bg-gray-800/30 dark:border-gray-700'
      }`}
    >
      <div className="flex items-start gap-3">
        {getTipIcon(tip.note_type)}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <Badge
              variant={tip.note_type === 'fail_reason' ? 'destructive' : 'outline'}
              className="text-xs"
            >
              {getNoteTypeLabel(tip.note_type)}
            </Badge>
            {tip.confidence_score >= 0.75 && (
              <Badge variant="secondary" className="text-xs gap-1">
                <CheckCircle2 className="h-3 w-3" />
                Verified
              </Badge>
            )}
          </div>
          <p className="text-sm">{tip.content}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex items-center gap-1 cursor-help">
                    <Users className="h-3 w-3" />
                    {tip.contributor_label}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Contributor reputation level</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {tip.same_here_count > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex items-center gap-1 cursor-help text-blue-600">
                      <MessageSquare className="h-3 w-3" />
                      {tip.same_here_count} confirmed
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      {tip.same_here_count} other contractor{tip.same_here_count !== 1 ? 's' : ''}{' '}
                      confirmed this tip
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Inline tip display for checklist items
 * Shows a small tip indicator next to items that have associated notes
 */
interface InlineTipProps {
  sourceType?: 'known_fact' | 'inspector_note' | 'baseline' | 'manual';
  sourceConfidence?: number;
  sameHereCount?: number;
  isValidated?: boolean;
}

export function InlineTipIndicator({
  sourceType,
  sourceConfidence,
  sameHereCount,
  isValidated,
}: InlineTipProps) {
  if (!sourceType || sourceType === 'baseline' || sourceType === 'manual') {
    return null;
  }

  const isFromNote = sourceType === 'inspector_note';
  const isFromFact = sourceType === 'known_fact';

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs cursor-help ${
              isFromNote
                ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
            }`}
          >
            {isFromNote ? (
              <MessageSquare className="h-3 w-3" />
            ) : (
              <Lightbulb className="h-3 w-3" />
            )}
            {isValidated && <CheckCircle2 className="h-3 w-3" />}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="font-medium mb-1">
            {isFromNote ? 'From Inspector Note' : 'From Known Fact'}
          </p>
          {sourceConfidence && (
            <p className="text-xs text-muted-foreground">
              Confidence: {Math.round(sourceConfidence * 100)}%
            </p>
          )}
          {isFromNote && sameHereCount !== undefined && sameHereCount > 0 && (
            <p className="text-xs text-muted-foreground">
              Confirmed by {sameHereCount} contractor{sameHereCount !== 1 ? 's' : ''}
            </p>
          )}
          {isValidated && (
            <p className="text-xs text-green-600 dark:text-green-400">Community verified</p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default ContextualTips;
